import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'

import { MinusCircleIcon } from 'images'

import QualifierDropdown from './QualifierDropdown'
import PlatformTags from './PlatformTags'
import { useRangeLocations, useRangeLocationsDispatch } from './RangeLocationsContextProvider'
import { actions, getPreferredRangeUnit, hasRadius } from './rangeLocationsReducer'
import RadiusInput from './RadiusInput'
import {
  MIN_RADIUS_MILES_WITH_TARGETING_LIMITATIONS,
  MIN_RADIUS_KILOMETERS_WITH_TARGETING_LIMITATIONS,
  MIN_RADIUS_MILES_WITH_TARGETING_LIMITATIONS_US_CA,
  MIN_RADIUS_KILOMETERS_WITH_TARGETING_LIMITATIONS_US_CA,
  MIN_RADIUS_MILES,
  MIN_RADIUS_KILOMETERS,
} from './rangeLocationsReducer'
import { activeTargetingPlatforms } from '../targeting'
import { useCampaignSetup } from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import { derivePlatformSelectionFromSetup } from 'components/Recommendations/Campaign/platform'

const countries = require('../countries.json')

function Location({ location, hasTargetingLimitations = false, availablePlatforms = [] }) {
  const {
    data: { key, name, region, country_code },
    qualifier,
    radius,
    platforms,
  } = location
  const dispatch = useRangeLocationsDispatch()

  const togglePlatform = useCallback(
    (platform) => {
      dispatch({ type: actions.togglePlatform, payload: { locationKey: key, platform } })
    },
    [key, dispatch],
  )

  const getMinRadius = useCallback(() => {
    if (!radius || !radius.unit ) return MIN_RADIUS_MILES

    if (!hasTargetingLimitations)
      return radius.unit === 'miles' ? MIN_RADIUS_MILES : MIN_RADIUS_KILOMETERS

    if (radius.unit === 'miles') {
      if (country_code === 'US' || country_code === 'CA')
        return MIN_RADIUS_MILES_WITH_TARGETING_LIMITATIONS_US_CA
      return MIN_RADIUS_MILES_WITH_TARGETING_LIMITATIONS
    }

    if (radius.unit === 'kilometers') {
      if (country_code === 'US' || country_code === 'CA')
        return MIN_RADIUS_KILOMETERS_WITH_TARGETING_LIMITATIONS_US_CA
      return MIN_RADIUS_KILOMETERS_WITH_TARGETING_LIMITATIONS
    }

    return radius.unit === 'miles' ? MIN_RADIUS_MILES : MIN_RADIUS_KILOMETERS
  }, [hasTargetingLimitations, country_code, radius])

  React.useEffect(() => {
    // make sure radius is not less than min radius
    if (!radius) return
    const minRadius = getMinRadius()
    if (radius.value < minRadius) {
      dispatch({
        type: actions.setLocationRadius,
        payload: {
          locationKey: key,
          radius: { value: minRadius, unit: radius.unit },
          minRadius,
        },
      })
    }
  }, [dispatch, key, radius, getMinRadius])

  const displayName = useMemo(() => {
    let display = name
    if (region && region !== name) {
      display += `, ${region}`
    }
    return display
  }, [name, region])

  const setQualifier = useCallback(
    (qualifier) => {
      dispatch({ type: actions.setQualifier, payload: { locationKey: key, qualifier } })
    },
    [key, dispatch],
  )

  const setRadius = useCallback(
    (radius) => {
      dispatch({
        type: actions.setLocationRadius,
        payload: {
          locationKey: key,
          radius: { value: radius, unit: getPreferredRangeUnit() },
          minRadius: getMinRadius(),
        },
      })
    },
    [key, dispatch, getMinRadius],
  )

  const removeLocation = useCallback(() => {
    dispatch({ type: actions.removeLocation, payload: { locationKey: key } })
  }, [key, dispatch])

  return (
    <div className="range-locations_summary__location">
      <div className="range-locations_summary__location__inner">
        <QualifierDropdown
          className="range-locations_summary__location__qualifier"
          value={qualifier}
          onChange={(value) => setQualifier(value)}
          hasTargetingLimitations={hasTargetingLimitations}
        />
        <span className="range-locations_summary__location-name">{displayName}</span>
        {hasRadius(location) && (
          <RadiusInput
            defaultValue={radius.value}
            unit={radius.unit}
            onChange={setRadius}
            minRadius={getMinRadius()}
          />
        )}
        {availablePlatforms.length > 1 && (
          <PlatformTags
            platforms={platforms}
            availablePlatforms={availablePlatforms}
            onToggle={togglePlatform}
          />
        )}
      </div>
      <i
        title="remove"
        className="range-locations_summary__location-remove"
        onClick={removeLocation}
      >
        <MinusCircleIcon />
      </i>
    </div>
  )
}

Location.propTypes = {
  location: PropTypes.shape({
    data: PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      region: PropTypes.string,
      country_code: PropTypes.string,
    }),
    qualifier: PropTypes.string.isRequired,
    radius: PropTypes.shape({
      value: PropTypes.number,
      unit: PropTypes.oneOf(['miles', 'kilometers']),
    }),
    platforms: PropTypes.shape({ fb: PropTypes.bool, google: PropTypes.bool }),
  }),
  hasTargetingLimitations: PropTypes.bool,
  availablePlatforms: PropTypes.arrayOf(PropTypes.string),
}

function Country({ countryName, countryGroupName, locations, hasTargetingLimitations = false, availablePlatforms = [] }) {
  return (
    <div className="range-locations_summary__country">
      <h6 className="range-locations_summary__country__name">{countryGroupName || countryName}</h6>
      {locations.map((location) => (
        <Location
          key={location.data.key}
          location={location}
          hasTargetingLimitations={hasTargetingLimitations}
          availablePlatforms={availablePlatforms}
        />
      ))}
    </div>
  )
}
Country.propTypes = {
  countryName: PropTypes.string,
  countryGroupName: PropTypes.string,
  locations: PropTypes.array.isRequired,
  hasTargetingLimitations: PropTypes.bool,
  availablePlatforms: PropTypes.arrayOf(PropTypes.string),
}

export default function LocationsSummary({ hasTargetingLimitations = false }) {
  const { locations } = useRangeLocations()
  const { setup } = useCampaignSetup()
  const availablePlatforms = useMemo(
    () => activeTargetingPlatforms(derivePlatformSelectionFromSetup(setup)),
    [setup],
  )
  const { includedList, excludedList } = locations.reduce(
    (acc, location) => {
      const { qualifier } = location
      // Determine the type of location
      const includedList = acc.includedList
      const excludedList = acc.excludedList
      const list = qualifier === 'include' ? includedList : excludedList

      list.push(location)

      return acc
    },
    { includedList: [], excludedList: [] },
  )

  const { groupedCountries, groupedCountryGroups } = useMemo(() => {
    const combinedLocations = [...includedList, ...excludedList]
    return groupLocations(combinedLocations)
  }, [includedList, excludedList])

  return (
    <div
      className={cn('range-locations_summary', {
        'range-locations_summary--empty': !locations.length,
      })}
    >
      {/* Render grouped countries */}
      {groupedCountries.map((country) => (
        <Country
          key={country.groupName}
          countryName={country.groupName}
          locations={country.locations}
          hasTargetingLimitations={hasTargetingLimitations}
          availablePlatforms={availablePlatforms}
        />
      ))}

      {/* Render grouped country groups */}
      {groupedCountryGroups.map((countryGroup) => (
        <Country
          key={countryGroup.groupName}
          countryGroupName={countryGroup.groupName}
          locations={countryGroup.locations}
          hasTargetingLimitations={hasTargetingLimitations}
          availablePlatforms={availablePlatforms}
        />
      ))}
    </div>
  )
}

LocationsSummary.propTypes = {
  hasTargetingLimitations: PropTypes.bool,
}

/**
 * @typedef {object} LocationGroup
 * @property {string} groupName - Display name for the group (country or group name)
 * @property {object[]} locations - List of locations under that group
 */

/**
 * Groups locations for display
 * @param {object[]} locations
 * @returns {{ groupedCountries: LocationGroup[], groupedCountryGroups: LocationGroup[] }}
 */
function groupLocations(locations) {
  return locations.reduce(
    (acc, item) => {
      const location = item.data
      const isCountryGroupLike =
        location.is_worldwide === true ||
        location.type === 'country_group' ||
        (Array.isArray(location.country_codes) && location.country_codes.length > 1)

      const countryName = location.country_name || countries[location.country_code]?.name || ''

      if (isCountryGroupLike) {
        // Group by group name
        const existingGroup = acc.groupedCountryGroups.find(
          (group) => group.groupName === location.name,
        )

        if (existingGroup) {
          existingGroup.locations.push(item)
        } else {
          acc.groupedCountryGroups.push({
            groupName: location.name,
            locations: [item],
          })
        }
      } else if (location.country_code) {
        // Group under country
        const existingCountry = acc.groupedCountries.find(
          (group) => group.groupName === countryName,
        )

        if (existingCountry) {
          existingCountry.locations.push(item)
        } else {
          acc.groupedCountries.push({
            groupName: countryName,
            locations: [item],
          })
        }
      } else {
        // Fallback: place in its own group by name
        acc.groupedCountryGroups.push({
          groupName: location.name,
          locations: [item],
        })
      }

      return acc
    },
    { groupedCountries: [], groupedCountryGroups: [] },
  )
}
